import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, User, Menu, X, Phone, MapPin } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { CATEGORIES } from '../mock/mockData';
import { getCatalogCategories } from '../services/catalogService';
import logo from '../assets/logo.png';

const LOGO_URL = logo;

const Header = () => {
  const { count } = useCart();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState(CATEGORIES);
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/shop?q=${encodeURIComponent(search)}`);
  };

  useEffect(() => {
    let mounted = true;
    getCatalogCategories().then(({ categories: catalogCategories }) => {
      if (mounted) setCategories(catalogCategories);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const navItems = useMemo(
    () => categories.slice(0, 6).map((category) => ({ label: category.name, cat: category.id })),
    [categories]
  );

  return (
    <header className="sticky top-0 z-50 bg-[#FAF7F2] border-b border-[#1F2A44]/10">
      {/* Announcement Bar */}
      <div className="bg-[#1F2A44] text-[#FAF7F2] text-[12px] md:text-[13px] py-2 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <p className="truncate">
            <span className="font-semibold text-[#E8B530]">Summer Sale</span> &nbsp;Save up to 75% OFF + Additional Discounts
          </p>
          <div className="hidden md:flex items-center gap-4 text-[12px]">
            <a href="tel:+918861841594" className="flex items-center gap-1 hover:text-[#E8B530] transition-colors"><Phone size={12} /> +91 88618 41594</a>
            <Link to="/stores" className="flex items-center gap-1 hover:text-[#E8B530] transition-colors"><MapPin size={12} /> Our Stores</Link>
          </div>
        </div>
      </div>

      {/* Main Bar */}
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
        <button className="md:hidden text-[#1F2A44]" onClick={() => setOpen(!open)}>
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>

<Link to="/" className="flex-shrink-0 overflow-visible">
  <img
    src={LOGO_URL}
    alt="FOSEN"
    className="h-14 md:h-16 w-auto object-contain scale-125"
  />
</Link>

        <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-xl mx-6 relative">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="text"
            placeholder="Search sofas, beds, dining..."
            className="w-full border border-[#1F2A44]/20 bg-white rounded-full px-5 py-2.5 text-sm text-[#1F2A44] placeholder:text-[#1F2A44]/50 focus:outline-none focus:ring-2 focus:ring-[#E8B530] focus:border-transparent transition"
          />
          <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#1F2A44] hover:text-[#E8B530] transition-colors">
            <Search size={18} />
          </button>
        </form>

        <div className="ml-auto flex items-center gap-4 text-[#1F2A44]">
          <Link to={user ? '/profile' : '/login'} aria-label="account" className="hidden md:block hover:text-[#E8B530] transition-colors"><User size={22} /></Link>
          <Link to="/cart" className="relative hover:text-[#E8B530] transition-colors">
            <ShoppingBag size={22} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#E8B530] text-[#1F2A44] text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{count}</span>
            )}
          </Link>
        </div>
      </div>

      {/* Nav */}
      <nav className="hidden md:block border-t border-[#1F2A44]/10 bg-[#FAF7F2]">
        <ul className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-8 py-3 text-[13px] font-medium tracking-wide uppercase text-[#1F2A44]">
          {navItems.map((n) => (
            <li key={n.cat} className="group relative">
              <Link to={`/shop/${n.cat}`} className="hover:text-[#E8B530] transition-colors">{n.label}</Link>
              <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[#E8B530] group-hover:w-full transition-all duration-300"></span>
            </li>
          ))}
          <li className="group relative">
            <Link to="/shop" className="hover:text-[#E8B530] transition-colors">All Furniture</Link>
            <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[#E8B530] group-hover:w-full transition-all duration-300"></span>
          </li>
          <li className="group relative">
            <Link to="/about" className="hover:text-[#E8B530] transition-colors">About</Link>
            <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[#E8B530] group-hover:w-full transition-all duration-300"></span>
          </li>
        </ul>
      </nav>

      {/* Mobile Drawer */}
      {open && (
        <div className="md:hidden bg-[#FAF7F2] border-t border-[#1F2A44]/10">
          <form onSubmit={onSearch} className="p-4">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              type="text"
              placeholder="Search..."
              className="w-full border border-[#1F2A44]/20 bg-white rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B530]"
            />
          </form>
          <ul className="px-4 pb-4 space-y-2">
            {[...navItems, { label: 'All Furniture', cat: '' }, { label: 'My Account', cat: 'account' }, { label: 'About', cat: 'about' }].map((n) => (
              <li key={n.label}>
                <Link
                  to={n.cat === 'about' ? '/about' : n.cat === 'account' ? (user ? '/profile' : '/login') : `/shop${n.cat ? '/' + n.cat : ''}`}
                  onClick={() => setOpen(false)}
                  className="block py-2 text-[#1F2A44] hover:text-[#E8B530] uppercase text-sm font-medium"
                >{n.label}</Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
};

export default Header;
